import React, { useState } from 'react'
import useSWR from 'swr'
import { apiV2, wrapResponse } from '../../../repositories/repository'
import { ArticleDto, AppLanguage } from '../../../sdk'
import { CompactTable } from '@table-library/react-table-library/compact'
import { getTheme } from '@table-library/react-table-library/mantine'
import { useTheme } from '@table-library/react-table-library/theme'
import ArticleForm from './ArticleForm'
import { useDispatch } from 'react-redux'
import { setErrorMsg, setSuccessMsg } from '../../../store/Auth/action'
import useTranslate from '../../../hooks/useTranslate'
import './Articles.scss'

const Articles: React.FC = () => {
  const dispatch = useDispatch()
  const translateLocalazy = useTranslate()
  const [editingArticle, setEditingArticle] = useState<ArticleDto | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [loadingAction, setLoadingAction] = useState<string | null>(null)

  const {data: articles, mutate} = useSWR(
    'api.article.findAllAdmin',
    async () => {
      const res = await wrapResponse(apiV2.article.findAllAdmin())
      if (res.success) {
        return res.data
      }
      return []
    },
    {revalidateOnFocus: false}
  )

  const theme = useTheme([
    getTheme(),
    {
      Table: `
        --data-table-library_grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 2fr) !important;
      `,
      Cell: `
        overflow: visible !important;
        & > div {
          overflow: visible !important;
        }
      `
    },
  ])

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    if (window.confirm(translateLocalazy('article.list.delete-confirm'))) {
      setLoadingAction(id)
      dispatch(setErrorMsg(''))
      dispatch(setSuccessMsg(''))
      const res = await wrapResponse(apiV2.article.remove(id))
      if (res.success) {
        dispatch(setSuccessMsg(translateLocalazy('article.list.delete-success')))
        await mutate()
      } else {
        dispatch(setErrorMsg(res.error || translateLocalazy('article.list.delete-error')))
      }
      setLoadingAction(null)
    }
  }

  const handleRelease = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    setLoadingAction(id)
    dispatch(setErrorMsg(''))
    dispatch(setSuccessMsg(''))
    const res = await wrapResponse(apiV2.article.release(id))
    if (res.success) {
      dispatch(setSuccessMsg(translateLocalazy('article.list.release-success')))
      await mutate()
    } else {
      dispatch(setErrorMsg(res.error || translateLocalazy('article.list.release-error')))
    }
    setLoadingAction(null)
  }

  const handleEdit = (e: React.MouseEvent, article: ArticleDto) => {
    e.stopPropagation()
    setEditingArticle(article)
    setIsFormOpen(true)
  }

  const columns = [
    {
      label: translateLocalazy('article.list.column-title'),
      renderCell: (item: ArticleDto) => {
        const enTranslation = item.translations?.find((t) => t.language === AppLanguage.EN)
        return enTranslation?.title || item.translations?.[0]?.title || translateLocalazy('article.list.untitled')
      },
    },
    {
      label: translateLocalazy('article.list.column-status'),
      renderCell: (item: ArticleDto) => (
        <span className={`status-badge status-badge--${item.released ? 'released' : 'draft'}`}>
          {item.released ? translateLocalazy('article.list.status-released') : translateLocalazy('article.list.status-draft')}
        </span>
      ),
    },
    {
      label: translateLocalazy('article.list.column-actions'),
      renderCell: (item: ArticleDto) => (
        <div className="actions-wrapper">
          <button
            className="g_btn g_btn--outlined action-btn"
            onClick={(e) => handleEdit(e, item)}
            disabled={loadingAction === item._id}
          >
            {translateLocalazy('article.list.btn-edit')}
          </button>
          {!item.released && (
            <button
              className="g_btn g_btn--primary action-btn"
              onClick={(e) => handleRelease(e, item._id as any)}
              disabled={loadingAction === item._id}
            >
              {translateLocalazy('article.list.btn-release')}
            </button>
          )}
          <button
            className="g_btn g_btn--outlined action-btn action-btn--delete"
            onClick={(e) => handleDelete(e, item._id as any)}
            disabled={loadingAction === item._id}
          >
            {translateLocalazy('article.list.btn-delete')}
          </button>
        </div>
      ),
    },
  ]

  if (isFormOpen) {
    return (
      <ArticleForm
        article={editingArticle || undefined}
        onSave={() => {
          setIsFormOpen(false)
          mutate()
        }}
        onCancel={() => {
          setIsFormOpen(false)
        }}
      />
    )
  }

  return (
    <div className="card-box patient-info articles-wrapper">
      <div className="box-title box-title-flex">
        <h3>{translateLocalazy('article.list.title')}</h3>
        <button
          className="g_btn g_btn--primary"
          onClick={() => {
            setEditingArticle(null)
            setIsFormOpen(true)
          }}
        >
          {translateLocalazy('article.list.btn-create')}
        </button>
      </div>

      <div className="table-container">
        {articles ? (
          <CompactTable columns={columns} data={{ nodes: articles }} theme={theme} />
        ) : (
          <div>{translateLocalazy('article.list.loading')}</div>
        )}
      </div>
    </div>
  )
}

export default Articles
